import { EditablePreviewWorld, ObjectRuntimeState, Vec3 } from '../types'
import { playSample } from './audio'
import { FRAME_SECONDS } from './constants'
import { CLEAR_BGM_URL } from './missionBgm'
import { FAIL_GAMEOVER_SFX } from './missionSfx'
import { killAvatarOnTouch } from './primitives/killAvatarOnTouch'
import { pickRandom } from './primitives/pickRandom'
import { createWarpPadHandler } from './primitives/warpPad'
import { findStateByName, nextTempId } from './sharedHelpers'
import { BehaviorContext, BehaviorHandler, MissionBehaviors } from './types'

/**
 * RJF06（M6）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJF06.rbxl）の敵ギミックを移植する:
 * - Enemy/AmericanEagle（トリさん）× 4:
 *   - Script: 3 秒ごとにモデル全体（SetPrimaryPartCFrame）を 90 度回転 →
 *     赤い視野（Collision パーツ）が Torso を中心にぐるぐる見渡す。
 *   - Collision/CollisionScript + ServerScriptService/EnemyScript:
 *     視野にキャラクターが触れると効果音（All It Takes Sting 2）を鳴らし、
 *     2 秒後に player:LoadCharacter()（リスポーン地点へ戻す）。
 * - Enemy/BabyNessie（ネッシー）NessieScript:
 *   - 初期 Z から {Z-10, Z, Z+10} の 3 候補を作り、3 秒ごとにランダムな
 *     候補へ横移動（テレポート）→ ④ボスのこうげき（炎をまとった赤い球、
 *     一辺 5/10/15 のランダム寸法）を PrimaryPart + (3,1,0) に生成する。
 *   - ④ボスのこうげき: AttackScript = Touched で Humanoid.Health = 0、
 *     DestroyScript = 10 秒後に自壊。Anchored なのでその場に留まる。
 * - Warp/ワープ元 × 3 WarpScript: キャラクターが触れると、同じ Attribute ID
 *   を持つ「ワープ先」の Position + (0,5,0) へテレポートする。
 * - Clear/ClearScript: キャラクターが触れると BGM を止めてクリア音を再生し、
 *   Clear パーツ自身を destroy する。
 *
 * プレビューでの近似・意図的な差異:
 * - クリアではループ BGM をクリア曲（ユメミライ）へ差し替える（実機の
 *   「BGM 停止 + クリア音」を、ループする実音源 BGM への切替で再現する）。
 * - 実機の視野接触は sting 再生 → 2 秒後に LoadCharacter（死亡演出なしの
 *   即リスポーン）。プレビューには「生きたまま強制リスポーン」の経路が
 *   ないため、体力 0（死亡演出 → スポーン復帰）で同じ結果に揃える。
 *   視野に見つかった合図は失敗音（実音源）で鳴らす。
 * - ④ボスのこうげきの Fire パーティクル寸法調整（Size.X==10/15 で増量）は
 *   プレビューの炎表現が寸法に追従するため省略する。
 */

/** トリさん 1 体の旋回プロファイル（1 回の旋回角と間隔）。 */
interface EagleRotationProfile {
  readonly stepRad: number
  readonly intervalSeconds: number
}

/** 既定の旋回（Lua: CFrame.Angles(0,0,math.rad(90)) + wait(3)）。 */
const DEFAULT_EAGLE_ROTATION: EagleRotationProfile = {
  stepRad: Math.PI / 2,
  intervalSeconds: 3,
}

/**
 * トリさんごとの旋回プロファイル。テンプレ rbxl の 4 体は同名モデルだが
 * 中の Script が異なる（Torso の初期位置で対応付ける）:
 * - (-10.47, -173) / (11.53, -173): 90 度ずつ 3 秒ごと（カクカク旋回）
 * - (1.53, -258): 3 度ずつ毎フレーム（なめらかに約 90 度/秒）
 * - (22.53, -67): -1 度ずつ毎フレーム（ゆっくり逆回転）
 */
const EAGLE_ROTATION_BY_POSITION: ReadonlyArray<{
  readonly x: number
  readonly z: number
  readonly profile: EagleRotationProfile
}> = [
  { x: -10.47, z: -173, profile: DEFAULT_EAGLE_ROTATION },
  { x: 11.53, z: -173, profile: DEFAULT_EAGLE_ROTATION },
  {
    x: 1.53,
    z: -258,
    profile: { stepRad: (3 * Math.PI) / 180, intervalSeconds: FRAME_SECONDS },
  },
  {
    x: 22.53,
    z: -67,
    profile: { stepRad: (-1 * Math.PI) / 180, intervalSeconds: FRAME_SECONDS },
  },
]

/** プロファイル対応付けの許容距離（studs）。再変換の丸め差を吸収する。 */
const EAGLE_PROFILE_MATCH_DISTANCE = 5

/** Torso の初期位置から旋回プロファイルを引く（未登録の配置は既定値）。 */
const profileForTorso = (position: {
  x: number
  z: number
}): EagleRotationProfile => {
  for (const entry of EAGLE_ROTATION_BY_POSITION) {
    const dx = entry.x - position.x
    const dz = entry.z - position.z
    if (
      dx * dx + dz * dz <=
      EAGLE_PROFILE_MATCH_DISTANCE * EAGLE_PROFILE_MATCH_DISTANCE
    ) {
      return entry.profile
    }
  }
  return DEFAULT_EAGLE_ROTATION
}

/**
 * トリさんの胴体（Torso）の判別幅。視野の回転中心になる。ネッシーの
 * Torso（幅 11.4）と区別するため、実測幅 1.4 を上回る余裕で切る。
 */
const EAGLE_TORSO_MAX_WIDTH = 2

/** 赤い視野パーツの名前接頭辞（rbxl: AmericanEagle/Collision）。 */
const VISION_NAME_PREFIX = 'Collision'

/**
 * 羽パーツの名前接頭辞（rbxl: AmericanEagle/Torso 子の RightWing/LeftWing。
 * Weld で Torso に固定されており、モデル回転時は Torso と一体で動く）。
 */
const WING_NAME_PREFIXES: ReadonlyArray<string> = ['RightWing', 'LeftWing']

/**
 * 羽と Torso の対応付けを許容する XZ 距離（studs）。羽は Torso の至近
 * （オフセット 1 stud 未満）にあるため、これより遠い同名接頭辞のパーツは
 * 鳥の羽とみなさない（誤って無関係なパーツを回さない安全弁）。
 */
const WING_TORSO_MAX_DISTANCE = 5

/** 視野パーツのシーン上の名前（Touched ハンドラのキー）。 */
const VISION_NAMES: ReadonlyArray<string> = [
  'Collision',
  'Collision#2',
  'Collision#3',
  'Collision#4',
]

/** ネッシーのテレポート候補 Z のオフセット（Lua: {posZ-10, posZ, posZ+10}）。 */
const NESSIE_Z_OFFSETS: ReadonlyArray<number> = [-10, 0, 10]

/** ネッシーの行動サイクル（Lua: wait(3)）。 */
const NESSIE_CYCLE_SECONDS = 3

/** ④ボスのこうげきの生成位置の Torso からのオフセット（Lua: +Vector3.new(3,1,0)）。 */
const FIREBALL_SPAWN_OFFSET: Vec3 = { x: 3, y: 1, z: 0 }

/** ④ボスのこうげきの一辺の候補（Lua: rand:NextInteger(1,3) * 5）。 */
const FIREBALL_SIZES: ReadonlyArray<number> = [5, 10, 15]

/** ④ボスのこうげきの寿命（Lua: DestroyScript wait(10)）。 */
const FIREBALL_LIFETIME_SECONDS = 10

/** ④ボスのこうげきの名前（Touched ハンドラのキー。シーンの雛形と同名）。 */
const FIREBALL_NAME = '④ボスのこうげき'

/** ④ボスのこうげきの色（rbxl 実測: 純赤）。 */
const FIREBALL_COLOR = '#FF0000'

/**
 * ④ボスのこうげきの当たり判定サイズの倍率。炎エフェクトが本体の球より大きく
 * 広がって見えるため、見た目の球より小さい当たり判定にして、火の玉のコアに
 * 触れたときだけ死ぬようにする（見た目の size 自体は変えない）。
 */
const FIREBALL_COLLISION_SCALE = 0.6

/**
 * ワープ元 → ワープ先の対応。rbxl では同名パーツ 3 組が Attribute ID(1〜3)
 * でペアになるが、シーン変換で重複名は文書順に #N 連番化される。実測座標で
 * 連番と ID の一致を確認済み（元: x=-50.5/-40.4/-30.4 が ID1〜3、
 * 先: z=-107/-217/-324 が ID1〜3）。
 */
const WARP_PAIRS: ReadonlyArray<{ from: string; to: string }> = [
  { from: 'ワープ元', to: 'ワープ先' },
  { from: 'ワープ元#2', to: 'ワープ先#2' },
  { from: 'ワープ元#3', to: 'ワープ先#3' },
]

/** クリア判定パーツ名（Lua: Clear/ClearScript）。 */
const CLEAR_NAME = 'Clear'

/** トリさんの視野（Collision）・羽（Wing）と回転中心の Torso の対応。 */
interface EaglePair {
  readonly visionId: string
  readonly torsoId: string
  readonly wingIds: ReadonlyArray<string>
}

/** position に XZ 距離で最も近い Torso を返す。 */
const nearestTorso = (
  torsos: ReadonlyArray<ObjectRuntimeState>,
  position: Vec3,
): ObjectRuntimeState | undefined => {
  let nearest: ObjectRuntimeState | undefined
  let nearestDistance = Infinity
  for (const torso of torsos) {
    const dx = torso.position.x - position.x
    const dz = torso.position.z - position.z
    const distance = dx * dx + dz * dz
    if (distance < nearestDistance) {
      nearestDistance = distance
      nearest = torso
    }
  }
  return nearest
}

/**
 * 視野・羽パーツそれぞれに最も近いトリさんの Torso を対応付ける。
 * 名前の連番（Torso#5 はネッシー）に依存せず、寸法と XZ 距離で解決する。
 */
const pairVisionsWithTorsos = (
  world: EditablePreviewWorld,
): ReadonlyArray<EaglePair> => {
  const states = [...world.getState().values()]
  const torsos = states.filter(
    (state) =>
      state.name.startsWith('Torso') && state.size.x <= EAGLE_TORSO_MAX_WIDTH,
  )
  const visions = states.filter((state) =>
    state.name.startsWith(VISION_NAME_PREFIX),
  )
  const wings = states.filter((state) =>
    WING_NAME_PREFIXES.some((prefix) => state.name.startsWith(prefix)),
  )
  // 羽は Torso の至近（オフセット 1 stud 未満）にあり、最近傍 Torso = 親で確定する。
  // ただし許容距離外の同名接頭辞パーツは羽とみなさない。
  const wingIdsByTorsoId = new Map<string, string[]>()
  for (const wing of wings) {
    const torso = nearestTorso(torsos, wing.position)
    if (!torso) {
      continue
    }
    const dx = torso.position.x - wing.position.x
    const dz = torso.position.z - wing.position.z
    if (dx * dx + dz * dz > WING_TORSO_MAX_DISTANCE * WING_TORSO_MAX_DISTANCE) {
      continue
    }
    const ids = wingIdsByTorsoId.get(torso.id) ?? []
    wingIdsByTorsoId.set(torso.id, [...ids, wing.id])
  }
  const pairs: EaglePair[] = []
  for (const vision of visions) {
    const torso = nearestTorso(torsos, vision.position)
    if (torso) {
      pairs.push({
        visionId: vision.id,
        torsoId: torso.id,
        wingIds: wingIdsByTorsoId.get(torso.id) ?? [],
      })
    }
  }
  return pairs
}

/**
 * 1 体ぶんの旋回を 1 ステップ進める（Torso は自転、視野と羽は Torso を
 * 中心とした公転 + 自転で Weld 固定の一体回転を再現する）。
 */
const rotateEagleOnce = (
  world: EditablePreviewWorld,
  pair: EaglePair,
  stepRad: number,
): void => {
  const torso = world.getState().get(pair.torsoId)
  if (!torso) {
    return
  }
  const cos = Math.cos(stepRad)
  const sin = Math.sin(stepRad)
  for (const partId of [pair.visionId, ...pair.wingIds]) {
    const part = world.getState().get(partId)
    if (!part) {
      continue
    }
    // 世界 +Y の stepRad 回転: (dx,dz) → (dx·cos + dz·sin, -dx·sin + dz·cos)。
    const dx = part.position.x - torso.position.x
    const dz = part.position.z - torso.position.z
    world.setPosition(partId, {
      x: torso.position.x + dx * cos + dz * sin,
      y: part.position.y,
      z: torso.position.z - dx * sin + dz * cos,
    })
    world.setRotationY(partId, part.rotationY + stepRad)
  }
  world.setRotationY(pair.torsoId, torso.rotationY + stepRad)
}

/**
 * トリさん 1 体の旋回ループ（Lua: AmericanEagle/Script）。プロファイルに従い
 * モデル全体を回転する。プレビューではモデル概念がないため、Torso はその場で、
 * 視野（Collision）は Torso を中心とした公転 + 自転で同じ動きを再現する。
 * Lua は「回転 → wait」の順なので、開始直後にも 1 回転る。
 */
const rotateEagleLoop = async (
  ctx: BehaviorContext,
  pair: EaglePair,
  profile: EagleRotationProfile,
): Promise<void> => {
  while (!ctx.signal.aborted) {
    rotateEagleOnce(ctx.world, pair, profile.stepRad)
    await ctx.wait(profile.intervalSeconds)
  }
}

/** 全トリさんの旋回を並行起動する。 */
const rotateEagles: BehaviorHandler = async (ctx) => {
  const { world } = ctx
  const pairs = pairVisionsWithTorsos(world)
  await Promise.all(
    pairs.map((pair) => {
      const torso = world.getState().get(pair.torsoId)
      const profile =
        torso ? profileForTorso(torso.position) : DEFAULT_EAGLE_ROTATION
      return rotateEagleLoop(ctx, pair, profile)
    }),
  )
}

/**
 * 視野接触（Lua: CollisionScript → EnemyScript）。実機は sting 再生 →
 * 2 秒後に LoadCharacter。プレビューは体力 0 で死亡演出 → スポーン復帰
 * （リスポーン地点に戻る、という結果を揃える近似）。
 */
const onVisionTouched: BehaviorHandler = (ctx) => {
  playSample(FAIL_GAMEOVER_SFX.url, FAIL_GAMEOVER_SFX.volume, ctx.signal)
  ctx.world.setAvatarHealth(0)
}

/**
 * ④ボスのこうげき（炎の球）を生成する（Lua: NessieScript attack()）。
 * 一辺 5/10/15 のランダム寸法・Anchored でその場に留まり、10 秒で自壊する。
 * 触れた判定は onTouchCharacter（FIREBALL_NAME）が体力 0 にする。
 */
const spawnFireball = (ctx: BehaviorContext, torsoPosition: Vec3): void => {
  const { world } = ctx
  const id = nextTempId(world, 'rjf06_fireball')
  const size = pickRandom(FIREBALL_SIZES)
  world.addObject({
    id,
    name: FIREBALL_NAME,
    position: {
      x: torsoPosition.x + FIREBALL_SPAWN_OFFSET.x,
      y: torsoPosition.y + FIREBALL_SPAWN_OFFSET.y,
      z: torsoPosition.z + FIREBALL_SPAWN_OFFSET.z,
    },
    size: { x: size, y: size, z: size },
    color: FIREBALL_COLOR,
    shape: 'ball',
    fire: true,
    isStatic: true,
    canCollide: true,
    canTouch: true,
    collisionScale: FIREBALL_COLLISION_SCALE,
  })
  // 実機は ServerStorage の雛形を Clone() するため、雛形に注入された学習者の
  // Script も複製され、workspace へ親付けされた時点で実行が始まる。学習者が
  // ④ボスのこうげき に付けたプログラムを生成された球でも実行して再現する。
  ctx.startObjectPrograms?.(id, FIREBALL_NAME)
  // 寿命満了・中断のどちらでも必ず破棄する（stop 後の残留防止）。
  // fire-and-forget だが未処理 Promise にならないようここで捕捉してログする。
  ;(async () => {
    try {
      await ctx.wait(FIREBALL_LIFETIME_SECONDS)
    } finally {
      if (world.exists(id)) {
        world.destroy(id)
      }
    }
  })().catch((error: unknown) => {
    console.error(`[preview] ④ボスのこうげきの破棄に失敗 (id=${id})`, error)
  })
}

/**
 * ネッシーの行動ループ（Lua: NessieScript move()）。初期 Z から候補
 * {Z-10, Z, Z+10} を作り、3 秒ごとにランダムな候補へ横移動して
 * ④ボスのこうげきを生成する。Lua は「移動 → attack → wait(3)」の順。
 */
const runNessie: BehaviorHandler = async (ctx) => {
  const { world } = ctx
  const torso = [...world.getState().values()].find(
    (state) =>
      state.name.startsWith('Torso') && state.size.x > EAGLE_TORSO_MAX_WIDTH,
  )
  if (!torso) {
    return
  }
  const torsoId = torso.id
  const candidates = NESSIE_Z_OFFSETS.map((offset) => torso.position.z + offset)
  while (!ctx.signal.aborted) {
    const current = world.getState().get(torsoId)
    if (!current) {
      return
    }
    const targetZ = pickRandom(candidates)
    world.setPosition(torsoId, {
      x: current.position.x,
      y: current.position.y,
      z: targetZ,
    })
    const moved = world.getState().get(torsoId)
    if (moved) {
      spawnFireball(ctx, moved.position)
    }
    await ctx.wait(NESSIE_CYCLE_SECONDS)
  }
}

export const createRjf06Behaviors = (): MissionBehaviors => {
  const onTouchCharacter = new Map<string, BehaviorHandler>()
  for (const name of VISION_NAMES) {
    onTouchCharacter.set(name, onVisionTouched)
  }
  // Lua: AttackScript。④ボスのこうげきに触れたら体力 0。
  onTouchCharacter.set(FIREBALL_NAME, killAvatarOnTouch)
  // Lua: WarpScript。ワープ元に触れたら対の ワープ先 + (0,5,0) へ移動。
  for (const pair of WARP_PAIRS) {
    onTouchCharacter.set(pair.from, createWarpPadHandler(pair.to))
  }
  // Lua: ClearScript。接触で BGM をクリア曲へ差し替え + Clear パーツ自壊
  // （以降は再発火しない）。
  onTouchCharacter.set(CLEAR_NAME, (ctx) => {
    const clear = findStateByName(ctx.world, CLEAR_NAME)
    if (!clear) {
      return
    }
    ctx.setBgm?.(CLEAR_BGM_URL)
    ctx.world.destroy(clear.id)
  })
  return {
    onPlay: [rotateEagles, runNessie],
    onTouchCharacter,
  }
}
